import { FacilitatorScript, CurveballStructured } from '../types/exercise';

/**
 * Parse timing string to extract minutes (backward compatibility)
 */
export function parseTimingToMinutes(timing?: string): number | undefined {
  if (!timing) return undefined;
  
  const match = timing.match(/(\d+)\s*min/i);
  return match ? parseInt(match[1], 10) : undefined;
}

/**
 * Map legacy ifTheyStall to probesIfStall (backward compatibility)
 */
export function mapIfTheyStallToProbes(ifTheyStall?: string[]): string[] | undefined {
  return ifTheyStall && ifTheyStall.length > 0 ? ifTheyStall : undefined;
}

/**
 * Map legacy curveballs to structured format (backward compatibility)
 */
export function mapLegacyCurveballs(curveballs?: string[] | CurveballStructured[]): CurveballStructured[] | undefined {
  if (!curveballs || curveballs.length === 0) return undefined;
  
  // Already structured
  if (typeof curveballs[0] === 'object') {
    return curveballs as CurveballStructured[];
  }
  
  // Convert string array to structured
  return (curveballs as string[]).map((text, index) => ({
    dropAtMin: 2 + (index * 5), // Default: drop every 5 minutes starting at minute 2
    text,
    intent: undefined
  }));
}

/**
 * Get enhanced facilitator script with backward compatibility
 */
export function getEnhancedFacilitatorScript(script: FacilitatorScript): FacilitatorScript {
  return {
    ...script,
    // Apply backward compatibility mappings
    timeboxMins: script.timeboxMins ?? parseTimingToMinutes(script.timing),
    probesIfStall: script.probesIfStall ?? mapIfTheyStallToProbes(script.ifTheyStall),
    curveballs: mapLegacyCurveballs(script.curveballs)
  };
}

/**
 * Check if script has enhanced fields (for rendering decisions)
 */
export function hasEnhancedFields(script: FacilitatorScript): boolean {
  return !!(
    script.slideTitle ||
    script.purpose ||
    script.timeboxMins ||
    script.expectedDecisions ||
    script.unknownsToSurface ||
    script.probesIfStall ||
    script.captureInstructions ||
    script.handoffToNext
  );
}

/**
 * Get display timing for a script (enhanced or legacy)
 */
export function getDisplayTiming(script: FacilitatorScript): string {
  if (script.timeboxMins) {
    return `${script.timeboxMins} minutes`;
  }
  
  if (script.timing) {
    return script.timing;
  }
  
  return 'Timing not specified';
}

/**
 * Get curveball display timing
 */
export function getCurveballTiming(curveball: CurveballStructured): string {
  return `T+${curveball.dropAtMin}min`;
}

